import { useState, type ReactNode } from 'react';
import { Calendar, Check, ChevronsUpDown } from 'lucide-react';
import { Button } from '@iplan/ui/primitives/button';
import { Popover, PopoverContent, PopoverTrigger } from '@iplan/ui/primitives/popover';
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@iplan/ui/primitives/command';
import { useEventContext } from '@/contexts/EventContext';
import type { EventOverview } from '@/pages/events/types';

/**
 * Builds the human-readable label for an event: `name` followed by its status
 * and (when present) its site. Mirrors iPlan4's `getEventDisplayLabel`, adapted
 * to the `EventsOverview` projection (`eventStatus` / `siteName`). Used for both
 * the trigger caption and the searchable list's per-item value (so the cmdk
 * filter matches name, status, and site).
 */
export function getEventDisplayLabel(event: EventOverview): string {
  const name = event.name?.trim() || 'Unnamed event';
  const parts = [name];
  if (event.eventStatus) parts.push(`(${event.eventStatus})`);
  if (event.siteName) parts.push(`— ${event.siteName}`);
  return parts.join(' ');
}

/**
 * Top-bar searchable event selector. Lets the user pick the working event from
 * anywhere in the app; the selection is persisted via `EventContext`. Never
 * auto-selects — the user always picks explicitly.
 */
export function EventSelector(): ReactNode {
  const {
    selectedEventId,
    setSelectedEventId,
    selectedEvent,
    events,
    isLoadingEvents,
    isErrorEvents,
    searchTerm,
    setSearchTerm,
  } = useEventContext();
  const [open, setOpen] = useState(false);

  // Loading / error states are surfaced on the trigger itself (disabled +
  // caption) so the user sees them without opening the popover. Empty data is
  // handled inside the list.
  const disabled = isLoadingEvents || isErrorEvents;
  const triggerLabel = isLoadingEvents
    ? 'Loading events…'
    : isErrorEvents
      ? 'Failed to load events'
      : selectedEvent
        ? getEventDisplayLabel(selectedEvent)
        : 'Select event';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          aria-haspopup="listbox"
          aria-expanded={open}
          disabled={disabled}
          className="w-[240px] justify-between gap-2"
          data-testid="event-selector-trigger"
        >
          <span className="flex min-w-0 items-center gap-2">
            <Calendar className="size-4 shrink-0 text-muted-foreground" aria-hidden />
            <span className={`truncate${selectedEvent ? '' : ' text-muted-foreground'}`}>
              {triggerLabel}
            </span>
          </span>
          <ChevronsUpDown className="size-4 shrink-0 opacity-50" aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0" align="end" data-testid="event-selector-popover">
        {/* Server-side search: cmdk's client filter is off; the input drives the
            context's `$filter` requery (top {PAGE_SIZE} matches from the server). */}
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search events…"
            value={searchTerm}
            onValueChange={setSearchTerm}
            data-testid="event-selector-search"
          />
          <CommandList>
            {events.length === 0 ? (
              <div
                className="px-3 py-6 text-center text-sm text-muted-foreground"
                data-testid="event-selector-empty"
              >
                {isLoadingEvents
                  ? 'Loading events…'
                  : searchTerm.trim()
                    ? 'No events match your search'
                    : 'No events available'}
              </div>
            ) : (
              <CommandGroup>
                {events.map((event) => {
                  const isSelected = selectedEventId === event.id;
                  return (
                    <CommandItem
                      key={event.id}
                      value={event.id}
                      onSelect={() => {
                        setSelectedEventId(event.id);
                        setOpen(false);
                      }}
                      className="gap-2"
                      data-testid={`event-selector-option-${event.id}`}
                    >
                      <Check
                        className={`size-4 shrink-0 ${isSelected ? 'opacity-100' : 'opacity-0'}`}
                        aria-hidden
                      />
                      <span className="flex min-w-0 flex-col">
                        <span className="truncate">{event.name?.trim() || 'Unnamed event'}</span>
                        <span className="truncate text-xs text-muted-foreground">
                          {[event.eventStatus, event.siteName].filter(Boolean).join(' · ')}
                        </span>
                      </span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
